import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import Modal from '../components/Modal';
import QuoteService from '../components/QuoteService';
import CompanyService from '../components/CompanyService';
import ProductService from '../components/ProductService';

const statusOptions = [
  'pending',
  'pending_approval',
  'accepted',
  'rejected',
  'cancelled',
  'completed',
];

const emptyTax = {
  tax_type: 'percent',
  value: '',
};

const emptyProduct = {
  product_id: '',
  product_name: '',
  product_image: '',
  quantity: 1,
  price: '',
  sale_price: '',
  optionsText: '{}',
  taxes: [{ ...emptyTax, value: 20 }],
  has_discount: false,
  discount_type: 'percentage',
  discount_value: '',
};

const emptyForm = {
  company_id: '',
  status: 'pending',
  expiry_date: '',
  terms: '',
  type: 'document',
  note: '',
  products: [{ ...emptyProduct }],
};

const extractList = (response) => response?.response || response || [];
const extractItem = (response) => response?.response || response?.data || response;

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const selectMenuProps = {
  disablePortal: true,
  keepMounted: true,
  PaperProps: {
    style: {
      maxHeight: 320,
    },
  },
};

function QuotesView() {
  const [quotes, setQuotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ status: '', company_id: '' });
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [pickerLineIndex, setPickerLineIndex] = useState(null);

  const resolveProductId = (product) => product?._id || product?.id || product?.product_id || '';
  const resolveProductName = (product) => product?.name || product?.label || product?.title || 'Produit';
  const resolveProductBarcode = (product) =>
    product?.barcode ||
    product?.pieceBarcodes?.[0] ||
    product?.ean13 ||
    product?.sku ||
    '';
  const resolveProductImageUrl = (product) => {
    const path = product?.url_product || product?.image || product?.thumbnail;
    if (!path) return '';
    const normalized = `${path}`.replace(/^\//, '');
    if (normalized.startsWith('http')) {
      return normalized;
    }
    return `${process.env.REACT_APP_DATA_PIC_PATH}/${normalized}`;
  };
  const resolveProductPrice = (product) => {
    const candidates = [
      product?.sale_price,
      product?.price,
      product?.regular_price,
      product?.priceNumber,
      product?.amount,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return 0;
  };

  const companyNameById = useMemo(() => {
    return companies.reduce((acc, company) => {
      const id = company?._id || company?.id;
      if (id) {
        acc[id] = company?.name || id;
      }
      return acc;
    }, {});
  }, [companies]);

  const filteredProducts = useMemo(() => {
    const query = String(productSearch || '').trim().toLowerCase();
    if (!query) {
      return products.slice(0, 50);
    }

    return products
      .filter((product) => {
        const name = resolveProductName(product).toLowerCase();
        const barcode = String(resolveProductBarcode(product)).toLowerCase();
        return name.includes(query) || barcode.includes(query);
      })
      .slice(0, 100);
  }, [productSearch, products]);

  const quoteSummary = useMemo(() => {
    const lines = Array.isArray(formData.products) ? formData.products : [];
    const subtotal = lines.reduce((sum, line) => {
      const quantity = Math.max(toNumber(line?.quantity, 1), 1);
      const unitPrice = Math.max(toNumber(line?.sale_price, line?.price), 0);
      let lineSubtotal = quantity * unitPrice;
      if (line?.has_discount && `${line?.discount_type || ''}` === 'percentage') {
        lineSubtotal -= (lineSubtotal * Math.max(toNumber(line?.discount_value, 0), 0)) / 100;
      } else if (line?.has_discount && `${line?.discount_type || ''}` === 'fixed') {
        lineSubtotal -= Math.max(toNumber(line?.discount_value, 0), 0);
      }
      return sum + Math.max(lineSubtotal, 0);
    }, 0);

    const total = lines.reduce((sum, line) => {
      const quantity = Math.max(toNumber(line?.quantity, 1), 1);
      const unitPrice = Math.max(toNumber(line?.sale_price, line?.price), 0);
      let lineSubtotal = quantity * unitPrice;
      if (line?.has_discount && `${line?.discount_type || ''}` === 'percentage') {
        lineSubtotal -= (lineSubtotal * Math.max(toNumber(line?.discount_value, 0), 0)) / 100;
      } else if (line?.has_discount && `${line?.discount_type || ''}` === 'fixed') {
        lineSubtotal -= Math.max(toNumber(line?.discount_value, 0), 0);
      }
      lineSubtotal = Math.max(lineSubtotal, 0);
      const taxTotal = (Array.isArray(line?.taxes) ? line.taxes : []).reduce((taxSum, tax) => {
        const value = Math.max(toNumber(tax?.value, 0), 0);
        if (`${tax?.tax_type || 'percent'}` === 'fix') {
          return taxSum + value;
        }
        return taxSum + ((lineSubtotal * value) / 100);
      }, 0);
      return sum + lineSubtotal + taxTotal;
    }, 0);

    return {
      subtotal: subtotal.toFixed(2),
      total: total.toFixed(2),
    };
  }, [formData.products]);

  const loadCompanies = async () => {
    try {
      const response = await CompanyService.fetchCompanies();
      const list = extractList(response);
      setCompanies(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Unable to load companies for quotes', err);
      setCompanies([]);
    }
  };

  const loadQuotes = async (nextFilters = filters) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await QuoteService.fetchQuotes(nextFilters);
      const list = extractList(response);
      setQuotes(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de charger les devis.');
      setQuotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const ensureProductsLoaded = async () => {
    if (products.length > 0 || isLoadingProducts) return;

    setIsLoadingProducts(true);
    try {
      const list = await ProductService.fetchProducts();
      setProducts(Array.isArray(list) ? list : []);
    } catch (err) {
      setError('Impossible de charger les produits.');
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    loadQuotes();
    ensureProductsLoaded();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, products: [{ ...emptyProduct }] });
    setError('');
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    loadQuotes(nextFilters);
  };

  const updateProduct = (index, key, value) => {
    setFormData((prev) => {
      const nextProducts = [...prev.products];
      nextProducts[index] = { ...nextProducts[index], [key]: value };
      return { ...prev, products: nextProducts };
    });
  };

  const addTax = (productIndex) => {
    setFormData((prev) => {
      const nextProducts = [...prev.products];
      const currentProduct = nextProducts[productIndex] || { ...emptyProduct };
      nextProducts[productIndex] = {
        ...currentProduct,
        taxes: [...(currentProduct.taxes || []), { ...emptyTax }],
      };
      return { ...prev, products: nextProducts };
    });
  };

  const updateTax = (productIndex, taxIndex, key, value) => {
    setFormData((prev) => {
      const nextProducts = [...prev.products];
      const currentProduct = nextProducts[productIndex] || { ...emptyProduct };
      const nextTaxes = [...(currentProduct.taxes || [])];
      nextTaxes[taxIndex] = { ...(nextTaxes[taxIndex] || { ...emptyTax }), [key]: value };
      nextProducts[productIndex] = {
        ...currentProduct,
        taxes: nextTaxes,
      };
      return { ...prev, products: nextProducts };
    });
  };

  const removeTax = (productIndex, taxIndex) => {
    setFormData((prev) => {
      const nextProducts = [...prev.products];
      const currentProduct = nextProducts[productIndex] || { ...emptyProduct };
      nextProducts[productIndex] = {
        ...currentProduct,
        taxes: (currentProduct.taxes || []).filter((_, index) => index !== taxIndex),
      };
      return { ...prev, products: nextProducts };
    });
  };

  const openProductPicker = async (lineIndex) => {
    setPickerLineIndex(lineIndex);
    setProductSearch('');
    await ensureProductsLoaded();
  };

  const handleProductPick = (product) => {
    if (pickerLineIndex === null || pickerLineIndex === undefined) return;

    const resolvedPrice = resolveProductPrice(product);
    const productId = resolveProductId(product);
    const productName = resolveProductName(product);

    setFormData((prev) => {
      const nextProducts = [...prev.products];
      const currentProduct = nextProducts[pickerLineIndex] || { ...emptyProduct };
      nextProducts[pickerLineIndex] = {
        ...currentProduct,
        product_id: productId,
        product_name: productName,
        product_image: resolveProductImageUrl(product),
        price: resolvedPrice || currentProduct.price || '',
        sale_price: '',
      };
      return { ...prev, products: nextProducts };
    });

    setPickerLineIndex(null);
    setProductSearch('');
  };

  const addProduct = () => {
    setFormData((prev) => ({ ...prev, products: [...prev.products, { ...emptyProduct }] }));
  };

  const removeProduct = (index) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.length === 1
        ? [{ ...emptyProduct }]
        : prev.products.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const mapQuoteToForm = (quote) => ({
    company_id: quote?.company_id || '',
    status: quote?.status || 'pending',
    expiry_date: quote?.data?.expiry_date || '',
    terms: quote?.data?.terms || '',
    type: quote?.type || quote?.data?.type || 'document',
    note: quote?.note || '',
    products: Array.isArray(quote?.products) && quote.products.length > 0
      ? quote.products.map((product) => ({
        product_id: product?.product_id || '',
        product_name: product?.product_name || '',
        product_image: product?.product_image || '',
        quantity: product?.quantity ?? 1,
        price: product?.price ?? '',
        sale_price: product?.sale_price ?? '',
        optionsText: JSON.stringify(product?.options || {}, null, 2),
        taxes: Array.isArray(product?.tax)
          ? product.tax.map((tax) => ({
            tax_type: tax?.tax_type || 'percent',
            value: tax?.value ?? '',
          }))
          : [{ ...emptyTax, value: 20 }],
        has_discount: product?.discount_value !== undefined && product?.discount_value !== null && String(product?.discount_value) !== '',
        discount_type: product?.discount_type || 'percentage',
        discount_value: product?.discount_value ?? '',
      }))
      : [{ ...emptyProduct }],
  });

  const handleEdit = async (quote) => {
    const id = quote?._id || quote?.id;
    if (!id) return;

    setError('');
    try {
      const response = await QuoteService.fetchQuote(id);
      const fullQuote = extractItem(response) || quote;
      setEditingId(id);
      setFormData(mapQuoteToForm(fullQuote));
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de charger le devis.');
    }
  };

  const buildPayload = () => {
    const productsPayload = formData.products
      .filter((product) => String(product.product_id || '').trim() !== '' || String(product.product_name || '').trim() !== '')
      .map((product) => ({
        ...(String(product.product_id || '').trim() !== '' ? { product_id: String(product.product_id || '').trim() } : {}),
        product_name: String(product.product_name || '').trim(),
        product_image: String(product.product_image || '').trim(),
        quantity: Math.max(toNumber(product.quantity, 1), 1),
        ...(String(product.price).trim() !== '' ? { price: Number(product.price) } : {}),
        ...(String(product.sale_price).trim() !== '' ? { sale_price: Number(product.sale_price) } : {}),
        ...(product.has_discount && String(product.discount_value).trim() !== '' ? { discount_value: Number(product.discount_value) } : {}),
        ...(product.has_discount && String(product.discount_value).trim() !== '' ? { discount_type: product.discount_type } : {}),
        options: product.optionsText ? JSON.parse(product.optionsText) : {},
        tax: (product.taxes || [])
          .filter((tax) => Number(tax?.value) > 0)
          .map((tax) => ({
            tax_type: tax.tax_type,
            value: Number(tax.value),
          })),
      }));

    return {
      company_id: formData.company_id,
      status: formData.status || 'pending',
      type: formData.type || 'document',
      note: formData.note,
      products: productsPayload,
      data: {
        ...(formData.expiry_date ? { expiry_date: formData.expiry_date } : {}),
        ...(formData.terms ? { terms: formData.terms } : {}),
      },
    };
  };

  const handleSubmit = async () => {
    if (!formData.company_id) return;

    setIsSubmitting(true);
    setError('');

    try {
      const payload = buildPayload();

      if (editingId) {
        await QuoteService.updateQuote(editingId, payload);
      } else {
        payload.status = 'pending';
        await QuoteService.createQuote(payload);
      }

      resetForm();
      await loadQuotes();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur lors de la sauvegarde du devis.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;

    setError('');
    try {
      await QuoteService.deleteQuote(id);
      await loadQuotes();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur lors de la suppression du devis.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1">Devis</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestion des devis par societe, statut et lignes produits.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadQuotes()} disabled={isLoading}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            data-bs-toggle="modal"
            data-bs-target="#quoteModal"
            onClick={resetForm}
          >
            Nouveau devis
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Select
            displayEmpty
            value={filters.status}
            onChange={(event) => handleFilterChange('status', event.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
            MenuProps={selectMenuProps}
          >
            <MenuItem value="">Tous les statuts</MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
          <Select
            displayEmpty
            value={filters.company_id}
            onChange={(event) => handleFilterChange('company_id', event.target.value)}
            size="small"
            sx={{ minWidth: 240 }}
            MenuProps={selectMenuProps}
          >
            <MenuItem value="">Toutes les societes</MenuItem>
            {companies.map((company) => {
              const id = company?._id || company?.id;
              return <MenuItem key={id} value={id}>{company?.name || id}</MenuItem>;
            })}
          </Select>
        </Stack>
      </Paper>

      <Paper elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Numero</TableCell>
                <TableCell>Societe</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Produits</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Expiration</TableCell>
                <TableCell>Cree le</TableCell>
                <TableCell>Historique</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">Aucun devis trouve.</TableCell>
                </TableRow>
              ) : null}

              {quotes.map((quote) => {
                const quoteId = quote?._id || quote?.id;
                const productCount = Array.isArray(quote?.products) ? quote.products.length : 0;
                const historyCount = Array.isArray(quote?.history) ? quote.history.length : 0;

                return (
                  <TableRow key={quoteId} hover>
                    <TableCell>{quote?.quote_number || '-'}</TableCell>
                    <TableCell>{companyNameById[quote?.company_id] || quote?.company_id || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={quote?.status || '-'} color={quote?.status === 'accepted' ? 'success' : quote?.status === 'rejected' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell>{productCount}</TableCell>
                    <TableCell>{quote?.type || quote?.data?.type || '-'}</TableCell>
                    <TableCell>{quote?.data?.summary?.total ?? '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{quote?.note || '-'}</TableCell>
                    <TableCell>{quote?.data?.expiry_date || '-'}</TableCell>
                    <TableCell>{formatDateTime(quote?.createdAt)}</TableCell>
                    <TableCell>{historyCount}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        size="small"
                        data-bs-toggle="modal"
                        data-bs-target="#quoteModal"
                        onClick={() => handleEdit(quote)}
                        aria-label="Modifier le devis"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(quoteId)}
                        aria-label="Supprimer le devis"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        modalId="quoteModal"
        modalType="modal-xxl"
        onSubmit={handleSubmit}
        submitLabel={isSubmitting ? 'Enregistrement...' : editingId ? 'Mettre a jour' : 'Creer'}
        submitDisabled={isSubmitting || !formData.company_id}
        title={editingId ? 'Modifier le devis' : 'Nouveau devis'}
        onClose={() => {
          resetForm();
          setPickerLineIndex(null);
          setProductSearch('');
        }}
      >
        <Box sx={{ display: 'grid', gap: 2 }}>
          {pickerLineIndex !== null ? (
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.main', bgcolor: '#f8fbff' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Typography variant="h6">
                  Choisir un produit pour la ligne #{pickerLineIndex + 1}
                </Typography>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    setPickerLineIndex(null);
                    setProductSearch('');
                  }}
                >
                  Fermer
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <TextField
                  label="Rechercher par nom ou barcode"
                  value={productSearch}
                  onChange={(event) => setProductSearch(event.target.value)}
                  fullWidth
                />

                {isLoadingProducts ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Paper variant="outlined">
                    <TableContainer sx={{ maxHeight: 360 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nom</TableCell>
                            <TableCell>Barcode</TableCell>
                            <TableCell>Prix</TableCell>
                            <TableCell align="right">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredProducts.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Aucun produit trouve.
                              </TableCell>
                            </TableRow>
                          ) : null}

                          {filteredProducts.map((product) => {
                            const productId = resolveProductId(product);
                            return (
                              <TableRow key={productId || `${resolveProductName(product)}-${resolveProductBarcode(product)}`} hover>
                                <TableCell>{resolveProductName(product)}</TableCell>
                                <TableCell>{resolveProductBarcode(product) || '-'}</TableCell>
                                <TableCell>{resolveProductPrice(product) || '-'}</TableCell>
                                <TableCell align="right">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleProductPick(product)}
                                  >
                                    Choisir
                                  </button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Stack>
            </Paper>
          ) : null}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Select
              value={formData.company_id}
              onChange={(event) => setFormData((prev) => ({ ...prev, company_id: event.target.value }))}
              displayEmpty
              size="small"
              sx={{ width: '100%' }}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="" disabled>Selectionner une societe</MenuItem>
              {companies.map((company) => {
                const id = company?._id || company?.id;
                return <MenuItem key={id} value={id}>{company?.name || id}</MenuItem>;
              })}
            </Select>
            <Select
              value={formData.status}
              onChange={(event) => setFormData((prev) => ({ ...prev, status: event.target.value || 'pending' }))}
              size="small"
              sx={{ width: '100%' }}
              MenuProps={selectMenuProps}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Date d'expiration"
              type="date"
              value={formData.expiry_date}
              onChange={(event) => setFormData((prev) => ({ ...prev, expiry_date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Conditions"
              value={formData.terms}
              onChange={(event) => setFormData((prev) => ({ ...prev, terms: event.target.value }))}
              fullWidth
            />
            <Select
              value={formData.type}
              onChange={(event) => setFormData((prev) => ({ ...prev, type: event.target.value }))}
              size="small"
              sx={{ width: '100%' }}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="document">document</MenuItem>
              <MenuItem value="presentation">presentation</MenuItem>
            </Select>
          </Stack>

          <TextField
            label="Note"
            value={formData.note}
            onChange={(event) => setFormData((prev) => ({ ...prev, note: event.target.value }))}
            multiline
            minRows={3}
            fullWidth
          />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Produits</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addProduct}>
                Ajouter une ligne
              </Button>
            </Stack>

            <Stack spacing={2}>
              {formData.products.map((product, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">Produit #{index + 1}</Typography>
                    <IconButton color="error" onClick={() => removeProduct(index)} aria-label="Supprimer la ligne">
                      <RemoveIcon />
                    </IconButton>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => openProductPicker(index)}
                      sx={{ minHeight: 56 }}
                    >
                      Choisir un produit
                    </Button>
                    <Box
                      sx={{
                        minHeight: 56,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        bgcolor: '#fafafa',
                        overflow: 'hidden',
                        p: 1,
                      }}
                    >
                      {product.product_image ? (
                        <img
                          src={product.product_image}
                          alt={product.product_name || 'Produit'}
                          style={{ maxHeight: 72, maxWidth: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aucune image
                        </Typography>
                      )}
                    </Box>
                    <TextField
                      label="Produit"
                      value={product.product_name || ''}
                      onChange={(event) => updateProduct(index, 'product_name', event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Quantite"
                      type="number"
                      value={product.quantity}
                      onChange={(event) => updateProduct(index, 'quantity', event.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                      fullWidth
                    />
                    <TextField
                      label="Price"
                      type="number"
                      value={product.price}
                      onChange={(event) => updateProduct(index, 'price', event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Sale Price"
                      type="number"
                      value={product.sale_price}
                      onChange={(event) => updateProduct(index, 'sale_price', event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Total ligne"
                      value={(() => {
                        const quantity = Math.max(toNumber(product.quantity, 1), 1);
                        const unitPrice = Math.max(toNumber(product.sale_price, product.price), 0);
                        return (quantity * unitPrice).toFixed(2);
                      })()}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Box>

                  <Stack spacing={2} mt={2}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={Boolean(product.has_discount)}
                          onChange={(event) => {
                            updateProduct(index, 'has_discount', event.target.checked);
                            if (!event.target.checked) {
                              updateProduct(index, 'discount_value', '');
                              updateProduct(index, 'discount_type', 'percentage');
                            }
                          }}
                        />
                      )}
                      label="Ajouter une remise"
                    />

                    {product.has_discount ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                          gap: 2,
                        }}
                      >
                        <Select
                          value={product.discount_type}
                          onChange={(event) => updateProduct(index, 'discount_type', event.target.value)}
                          size="small"
                          sx={{ width: '100%' }}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value="percentage">percentage</MenuItem>
                          <MenuItem value="fixed">fixed</MenuItem>
                        </Select>
                        <TextField
                          label="Discount Value"
                          type="number"
                          value={product.discount_value}
                          onChange={(event) => updateProduct(index, 'discount_value', event.target.value)}
                          fullWidth
                        />
                      </Box>
                    ) : null}

                    <TextField
                      label="Options JSON"
                      value={product.optionsText}
                      onChange={(event) => updateProduct(index, 'optionsText', event.target.value)}
                      multiline
                      minRows={4}
                      fullWidth
                    />
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle2">Taxes</Typography>
                        <Button variant="text" startIcon={<AddIcon />} onClick={() => addTax(index)}>
                          Ajouter une taxe
                        </Button>
                      </Stack>

                      <Stack spacing={2}>
                        {(product.taxes || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Aucune taxe ajoutee.
                          </Typography>
                        ) : null}

                        {(product.taxes || []).map((tax, taxIndex) => (
                          <Stack key={taxIndex} direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <Select
                              value={tax.tax_type || 'percent'}
                              onChange={(event) => updateTax(index, taxIndex, 'tax_type', event.target.value)}
                              size="small"
                              sx={{ minWidth: 160 }}
                              MenuProps={selectMenuProps}
                            >
                              <MenuItem value="percent">percent</MenuItem>
                              <MenuItem value="fix">fix</MenuItem>
                            </Select>
                            <TextField
                              label={tax.tax_type === 'fix' ? 'Montant' : 'Pourcentage'}
                              type="number"
                              value={tax.value}
                              onChange={(event) => updateTax(index, taxIndex, 'value', event.target.value)}
                              inputProps={{ min: 0, step: 'any' }}
                              helperText={tax.tax_type === 'fix' ? 'Montant fixe de taxe' : 'Valeur > 0'}
                              fullWidth
                            />
                            <IconButton color="error" onClick={() => removeTax(index, taxIndex)} aria-label="Supprimer la taxe">
                              <RemoveIcon />
                            </IconButton>
                          </Stack>
                        ))}
                      </Stack>
                    </Paper>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#fafafa' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Typography variant="subtitle1">Sous-total: {quoteSummary.subtotal}</Typography>
                <Typography variant="subtitle1">Total: {quoteSummary.total}</Typography>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default QuotesView;
